export type AuthMode = 'LOCAL' | 'SSO'

export interface SsoProvider {
  name: string
  display_name: string
  login_url: string
}

export interface AuthConfig {
  auth_mode: AuthMode
  providers: SsoProvider[]
  /**
   * true  — a CONFIDENT config was obtained (genuine LOCAL mode, or SSO with at
   *         least one provider). Safe to render the login UI definitively.
   * false — every attempt failed or returned a degraded SSO-with-no-providers
   *         response (e.g. backend mid-rollout / transient Keycloak-discovery
   *         blip). The UI must NOT fall back to the local form on this — it
   *         should show a retry state, otherwise a brief blip strands the user
   *         on local login until a manual page reload.
   */
  ok: boolean
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082'

// We cache ONLY a confident result. A degraded/failed fetch is never cached, so
// the next caller (or a refetch) re-attempts instead of being stuck forever.
let cachedAuthMode: AuthMode | null = null
let cachedProviders: SsoProvider[] = []

const MAX_ATTEMPTS = 4
const BASE_DELAY_MS = 400

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

/**
 * Fetch /auth/config with retry+backoff. Returns a confident config (ok=true)
 * once obtained, or ok=false after exhausting retries. A degraded SSO response
 * (auth_mode=SSO but zero providers — what the backend serves while the SSO
 * registry is briefly unavailable) is treated as retryable, NOT as "show local
 * form", which is the flap the prod rollout exposed.
 */
export async function fetchAuthConfig(): Promise<AuthConfig> {
  if (cachedAuthMode !== null) {
    return { auth_mode: cachedAuthMode, providers: cachedProviders, ok: true }
  }

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      const response = await fetch(`${API_URL}/api/v1/auth/config`)
      if (!response.ok) {
        throw new Error(`auth/config HTTP ${response.status}`)
      }
      const data = await response.json()
      const mode: AuthMode = data?.data?.auth_mode === 'LOCAL' ? 'LOCAL' : 'SSO'
      const providers: SsoProvider[] = Array.isArray(data?.data?.providers)
        ? data.data.providers
        : []

      // Confident result: a real LOCAL deployment, or SSO with providers.
      // Cache it and return.
      if (mode === 'LOCAL' || providers.length > 0) {
        cachedAuthMode = mode
        cachedProviders = providers
        return { auth_mode: mode, providers, ok: true }
      }
      // SSO with no providers → degraded/transient. Fall through to retry.
    } catch {
      // Network/HTTP error → retry.
    }

    if (attempt < MAX_ATTEMPTS - 1) {
      await sleep(BASE_DELAY_MS * 2 ** attempt) // 400, 800, 1600ms
    }
  }

  // Exhausted without a confident answer. Do NOT cache, so a later refetch
  // retries once the backend is back. Report ok=false so the UI shows a retry
  // state instead of silently offering local login in an SSO deployment.
  return { auth_mode: 'SSO', providers: [], ok: false }
}

/** @deprecated Use fetchAuthConfig() instead */
export async function fetchAuthMode(): Promise<AuthMode> {
  const config = await fetchAuthConfig()
  return config.auth_mode
}

export function getAuthMode(): AuthMode {
  return cachedAuthMode ?? 'SSO'
}

/** Reset cached auth config. Use in tests only. */
export function resetAuthModeCache(): void {
  cachedAuthMode = null
  cachedProviders = []
}
